import {
  buildManagePageQueryStringWithClientTypeSearch,
  buildOrderGridQueryString,
} from "./helpers/buildManagePageQueryString";
import { jumboMediaAPI } from "./service";
import { CACHE_TTL } from "./cacheConfig";

import type { ClientT } from "../models/models";
import type {
  GetPredefinedUserDataPayloadT,
  GetPredefinedUserDataResultT,
  GetRegistrationClientTokenPayloadT,
  GetRegistrationClientTokenResultT,
  RegisterClientPayloadT,
  RegisterClientResultT,
} from "./models/models";

export const clientAPI = jumboMediaAPI.injectEndpoints({
  endpoints: (build) => ({
    getUserInfo: build.query({
      query: () => `api/Clients/GetUserInfo`,
    }),
    getClients: build.query({
      query: (body) =>
        `/api/Clients/DataTablesSource${buildManagePageQueryStringWithClientTypeSearch(
          body.columns,
          body.page,
          body.searchTerm,
          body.clientTypeSearchTerm,
          (body.page - 1) * body.limit,
          body.limit,
          body.orderColumnIndex,
          body.isDescSortDir,
          body.status
        )}`,
      providesTags: ["Clients"],
    }),
    getClientsForTable: build.query({
      query: (body) => ({
        // spec-31: resolve-by-name. The old builder placed the client-type filter on `index === 2` and
        // status on `index === 9`; reordering or slimming the columns array silently retargeted them.
        // Each value here lands on the column whose `value` matches the backend field name that
        // ClientGridFilters reads.
        url: `/api/Clients/DataTablesSource${buildOrderGridQueryString(
          body.columns,
          body.page,
          body.searchTerm,
          (body.page - 1) * body.limit,
          body.limit,
          body.orderColumnIndex,
          body.isDescSortDir,
          {
            ClientType: body.clientType,
            Status: body.status,
            ServicePackageId: body.servicePackage,
          }
        )}`,
        responseHandler: (res) =>
          res.json().then((res) => {
            const data = res.data.map((item: ClientT) => ({
              ...item,
              ...item?.ClientInfo,
              Id: item?.Id,
            }));
            return { ...res, data };
          }),
      }),
      providesTags: ["Clients"],
    }),
    getAllClients: build.query({
      query: (body) => ({
        url: `/api/Clients/UserGet?shareToken=${encodeURIComponent(
          body.shareToken || ""
        )}&withOnlyParentClients=${
          body?.withOnlyParentClients ? "true" : "false"
        }`,
        responseHandler: (res: any) =>
          res.json().then((res: any) => {
            if (body?.asSelectOptions) {
              return res.map((item: any) => ({
                value: item.Id,
                label: item.Name,
              }));
            } else {
              return res;
              // .map((item: any) => ({ id: item.Id, name: item.Name }));
            }
          }),
      }),
      providesTags: ["Clients"],
      keepUnusedDataFor: CACHE_TTL.LOOKUP,
    }),
    // spec-33: every client as {Id, Name, Status}, for the /admin/users company filter and for that
    // grid's Client column. Deliberately NOT getAllClients — /api/Clients/UserGet returns only Enabled
    // clients and, for a GlobalAdmin, drops out-of-IL broadcasters/post-production, hiding 61 clients
    // and 71 users. GlobalAdmin-only, so this is only ever called from /admin/users.
    getAllClientNames: build.query({
      query: () => `/api/Clients/AdminGetNames`,
      providesTags: ["Clients"],
    }),
    getAllAdvertisingAgency: build.query({
      query: (body) => ({
        url: `/api/Clients/UserAdvertiserAgencyGet`,
        responseHandler: (res: any) =>
          res.json().then((res: any) => {
            if (body?.asSelectOptions) {
              return res.map((item: any) => ({
                value: item.Id,
                label: item.Name,
              }));
            } else {
              return res;
              // .map((item: any) => ({ id: item.Id, name: item.Name }))
            }
          }),
      }),
    }),
    getAllProductionCompanies: build.query({
      query: (body) => ({
        url: `/api/Clients/GetProductionCompanies?`,
        responseHandler: (res: any) =>
          res.json().then((res: any) => {
            if (body?.asSelectOptions) {
              return res.map((item: any) => ({
                value: item.Id,
                label: item.Name,
              }));
            } else {
              return res;
              // .map((item: any) => ({ id: item.Id, name: item.Name }))
            }
          }),
      }),
    }),
    createClient: build.mutation({
      query: (body) => {
        return {
          url: `/api/Clients/AdminPost`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Clients"],
    }),
    // Inline "+ create new media company" from the campaign wizard.
    // Body: { Name, ContactName, Email, Phone } - the last three optional (empty string = absent).
    // Invalidating "Clients" refetches getAllClients so the new media company appears in
    // mediaCompaniesSO and can be auto-selected.
    createMediaCompanyShort: build.mutation({
      query: (body) => ({
        url: `api/Clients/UserPostMediaCompany`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Clients"],
    }),
    updateClient: build.mutation({
      query: (body) => {
        return {
          url: `/api/Clients/AdminPut/${body.Id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["Clients"],
    }),

    updateClientUser: build.mutation({
      query: (body) => {
        return {
          url: `/api/Clients/UserPut/${body.Id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["Clients"],
    }),

    deleteClient: build.mutation({
      query: (body) => ({
        url: `/api/Clients/AdminDelete/${body.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Clients"],
    }),
    getClientByUserId: build.query({
      query: (body) => `/api/Clients/UserGetByUserId/${body.Id}`,
    }),
    getAdminClientByUserId: build.query({
      query: (body) => `/api/Clients/AdminGetByUserId/${body.Id}`,
    }),
    getAdminClientByClientId: build.query({
      query: (body) => `/api/Clients/AdminGetClientById/${body.Id}`,
      providesTags: ["Clients"],
    }),
    // getGeneralReport: build.query({
    //     query: ({ downloadedOnly = false, sentWithPricing = true, year, month, pageNumber = 0, itemsPerPage = 200 }) => ({
    //         url: `/api/Clients/AdminGetCreatedGeneralClientLog?downloadedOnly=${downloadedOnly}&sentWithPricing=${sentWithPricing}&year=${year}&month=${month}&pageNumber=${pageNumber}&itemsPerPage=${itemsPerPage}&projectType=VideoAd`,
    //     }),
    // }),
    getPostProductionReport: build.query({
      query: ({
        year,
        month,
        clientId = 1065,
        pageNumber = 0,
        itemsPerPage = 200,
      }) => ({
        url: `/api/Clients/AdminGetCreatedProjectLog?clientId=${clientId}&downloadedOnly=false&sentWithPricing=true&year=${year}&month=${month}&pageNumber=${pageNumber}&itemsPerPage=${itemsPerPage}&projectType=VideoAd&isGlobalServicePackage=true`,
      }),
    }),
    exportPostProductionReport: build.mutation({
      query: (body) => ({
        url: `/api/Clients/AdminGetCreatedClientLogExcelFile`,
        method: "POST",
        body,
      }),
    }),
    getRegistrationClientToken: build.mutation<
      GetRegistrationClientTokenResultT,
      GetRegistrationClientTokenPayloadT
    >({
      query: (body) => ({
        url: `/api/Clients/GetRegistrationToken`,
        method: "POST",
        body,
      }),
    }),
    getPredefinedUserData: build.query<
      GetPredefinedUserDataResultT,
      GetPredefinedUserDataPayloadT
    >({
      query: (body) => ({
        url: `/api/Clients/GetPredefinedUserData?token=${encodeURIComponent(
          body.token
        )}`,
      }),
    }),
    registerClient: build.mutation<
      RegisterClientResultT,
      RegisterClientPayloadT
    >({
      query: (body) => ({
        url: `/api/Clients/RegisterClient`,
        method: "POST",
        body,
      }),
    }),
    getBroadcastersOverlayPlayer: build.query({
      query: ({projectId}) => ({
        url: `api/Clients/GetBroadcasterOverlayForPlayer?projectId=${projectId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetClientsQuery,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useGetUserInfoQuery,
  useLazyGetUserInfoQuery,
  useGetAllClientsQuery,
  useLazyGetAllClientsQuery,
  useGetAllClientNamesQuery,
  useCreateClientMutation,
  useCreateMediaCompanyShortMutation,
  useGetAllAdvertisingAgencyQuery,
  useGetAllProductionCompaniesQuery,
  useGetClientByUserIdQuery,
  useUpdateClientUserMutation,
  useGetPostProductionReportQuery,
  useExportPostProductionReportMutation,
  useGetClientsForTableQuery,
  useGetAdminClientByUserIdQuery,
  useGetAdminClientByClientIdQuery,
  useGetRegistrationClientTokenMutation,
  useLazyGetPredefinedUserDataQuery,
  useRegisterClientMutation,
  useGetBroadcastersOverlayPlayerQuery,
  useLazyGetBroadcastersOverlayPlayerQuery,
} = clientAPI;
